import Add16 from '@carbon/icons-react/es/add/16';
import { useBoolean, useInViewport } from 'ahooks';
import React, {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled, { css } from 'styled-components';
import { ifNotProp, ifProp } from 'styled-tools';
import { CreateFormQuestionPayload } from 'api/form';
import {
  FormGroupColor,
  FormQuestion,
  FormQuestionMetaValue,
} from 'api/form/types';
import { Loading } from 'components/atoms/loading';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Select } from 'components/atoms/select';
import { OptionProps } from 'components/atoms/select/types';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { useTranslate } from 'hooks/use-translation';
import { useCreateFieldQuestionMutation } from 'queries/form-template';
import { pascalCase } from 'utils/pascal-case';
import { FormQuestionsList } from './constants';
import {
  constructDefaultQuestionValues,
  useFormTemplateProjectFieldsList,
  useFormTemplateTaskFieldsList,
} from './utils';

const QuestionOptionItem = styled.div`
  display: flex;
  margin-top: var(--spacing-01);
`;
const QuestionOptionItemIcon = styled.div<{ backgroundColor: string }>`
  padding: var(--spacing-02);
  display: flex;
  align-items: center;
  border-radius: 5px;
  margin-right: var(--spacing-03);
  background: ${({ backgroundColor }) => backgroundColor};
`;
/* adding a higher z-index so that matrix question type table doesn't get placed on top
of the select menu dropdown */
const StyledSelect = styled(Select)`
  z-index: var(--zindex-elevated);
`;

const FormQuestionOption = (props: OptionProps): JSX.Element => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <QuestionOptionItem>
        <QuestionOptionItemIcon
          backgroundColor={FormGroupColor[pascalCase(data.group)]}
        >
          {data.icon}
        </QuestionOptionItemIcon>
        {data.label}
      </QuestionOptionItem>
    </components.Option>
  );
};

const FormQuestionSelectWrapper = styled.div`
  margin-top: var(--spacing-05);
  flex-grow: 1;
`;
const QuestionLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 120px;
  background: var(--white-100);
  justify-content: center;
  flex-direction: column;
  margin-top: var(--spacing-05);
`;

export const FormQuestionLoading = ({
  centerColWrapperRef,
}: {
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
}): JSX.Element => {
  const loadingRef = useRef<HTMLDivElement>(null);

  const inViewport = useInViewport(loadingRef);

  useEffect(() => {
    if (loadingRef.current && centerColWrapperRef?.current) {
      const loadingOffset = loadingRef.current.offsetTop;
      const wrapperScrollTop = centerColWrapperRef.current.scrollTop;
      const wrapperHeight = centerColWrapperRef.current.clientHeight;
      const isItemInViewPort =
        loadingOffset >= wrapperScrollTop &&
        loadingOffset < wrapperScrollTop + wrapperHeight;

      if (loadingOffset !== 0 && !isItemInViewPort) {
        centerColWrapperRef.current.scrollTo({
          top: loadingRef.current.offsetTop - 60,
          behavior: 'smooth',
        });
      }
    }
  }, [centerColWrapperRef, inViewport]);

  return (
    <QuestionLoadingWrapper ref={loadingRef}>
      <Loading />
      <FormattedMessage id="loading" />
    </QuestionLoadingWrapper>
  );
};

export const FormQuestionSelect = ({
  templateId,
  branchingFrom,
  position,
  onQuestionSelectSuccess,
  setSelectedFormItem,
  centerColWrapperRef,
}: {
  templateId: number;
  branchingFrom?: number;
  position?: number;
  onQuestionSelectSuccess?: () => void;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
}): JSX.Element => {
  const { mutate: createQuestionMutation, isLoading } =
    useCreateFieldQuestionMutation(templateId);

  const translate = useTranslate();

  const [availableProjectCustomFields, usedProjectCustomFields] =
    useFormTemplateProjectFieldsList(templateId);

  const [availableTaskCustomFields, usedTaskCustomFields] =
    useFormTemplateTaskFieldsList(templateId);

  const formSelectOptions = useMemo(() => {
    return [
      {
        label: translate('form_templates.form_fields'),
        options: FormQuestionsList.map((item) => {
          const labelKey = `form_templates.question.group.item.${item.label}`;

          return {
            ...item,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            label: translate(labelKey),
          };
        }),
      },
      {
        label: translate('project_fields'),
        options: availableProjectCustomFields.map((item) => {
          return {
            ...item,
            group: item.formQuestionGroup,
            label: item.fieldLabel,
            icon: item.formQuestionIcon,
            value: item.formQuestionType,
            fieldType: 'PROJECT_FIELD',
          };
        }),
      },
      {
        label: translate('form_template.project_field_used'),
        options: usedProjectCustomFields.map((item) => {
          return {
            ...item,
            group: item.formQuestionGroup,
            label: item.fieldLabel,
            icon: item.formQuestionIcon,
            value: item.formQuestionType,
            isDisabled: true,
            fieldType: 'PROJECT_FIELD',
          };
        }),
      },
      {
        label: translate('task_fields'),
        options: availableTaskCustomFields.map((item) => {
          return {
            ...item,
            group: item.formQuestionGroup,
            label: item.fieldLabel,
            icon: item.formQuestionIcon,
            value: item.formQuestionType,
            fieldType: 'TASK_FIELD',
          };
        }),
      },
      {
        label: translate('form_template.task_field_used'),
        options: usedTaskCustomFields.map((item) => {
          return {
            ...item,
            group: item.formQuestionGroup,
            label: item.fieldLabel,
            icon: item.formQuestionIcon,
            value: item.formQuestionType,
            isDisabled: true,
            fieldType: 'TASK_FIELD',
          };
        }),
      },
    ];
  }, [
    translate,
    availableProjectCustomFields,
    usedProjectCustomFields,
    availableTaskCustomFields,
    usedTaskCustomFields,
  ]);

  return (
    <FormQuestionSelectWrapper>
      {isLoading ? (
        <FormQuestionLoading centerColWrapperRef={centerColWrapperRef} />
      ) : (
        <StyledSelect
          menuIsOpen
          autoFocus
          placeholder={<FormattedMessage id="form_templates.choose_field" />}
          maxMenuHeight={225}
          options={formSelectOptions}
          components={{
            Option: FormQuestionOption,
          }}
          onChange={(selectField) => {
            let payload = {} as CreateFormQuestionPayload;

            if (selectField.fieldType === 'PROJECT_FIELD') {
              payload = {
                formQuestionType: selectField.formQuestionType,
                projectFieldId: selectField.fieldId,
                templateId,
                questionText: selectField.fieldLabel || '',
                description: selectField.description || '',
                branchingFrom,
                ...(selectField.formQuestionGroup === 'RATING'
                  ? {
                      questionMeta: {
                        numberOfStar: selectField.metaData?.ratingScale,
                        type: FormQuestionMetaValue.StarRating,
                      },
                    }
                  : {}),
              };
            } else if (selectField.fieldType === 'TASK_FIELD') {
              payload = {
                formQuestionType: selectField.formQuestionType,
                taskFieldId: selectField.fieldId,
                templateId,
                questionText: selectField.fieldLabel || '',
                description: selectField.description || '',
                branchingFrom,
              };
            } else {
              payload = constructDefaultQuestionValues({
                type: selectField.value,
                branchingFrom,
                position,
                templateId,
              });
            }

            createQuestionMutation(payload, {
              onSuccess: (questionResponse) => {
                onQuestionSelectSuccess?.();
                setSelectedFormItem(questionResponse);
              },
            });
          }}
        />
      )}
    </FormQuestionSelectWrapper>
  );
};

const FormDropAreaWrapper = styled.div`
  position: relative;
  margin-top: var(--spacing-05);
`;
const DropArea = styled.div`
  background: var(--blue-10);
  border: 2px dashed var(--blue-30);
  height: var(--spacing-11);
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  ${TertiaryButton} {
    margin-bottom: var(--spacing-02);
  }
`;

export const FormDropArea = ({
  templateId,
  centerColWrapperRef,
  setSelectedFormItem,
}: {
  templateId: number;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
}): JSX.Element => {
  const [
    isShowingQuestionSelect,
    { setTrue: showQuestionSelect, setFalse: hideQuestionSelect },
  ] = useBoolean(false);

  return (
    <FormDropAreaWrapper>
      {isShowingQuestionSelect ? (
        <OutsideClickHandler onOutsideClick={() => hideQuestionSelect()}>
          <FormQuestionSelect
            centerColWrapperRef={centerColWrapperRef}
            templateId={templateId}
            onQuestionSelectSuccess={() => hideQuestionSelect()}
            setSelectedFormItem={setSelectedFormItem}
          />
        </OutsideClickHandler>
      ) : (
        <DropArea>
          <TertiaryButton
            type="default"
            size="small"
            iconPlacement="right"
            icon={<Add16 />}
            onClick={() => {
              showQuestionSelect();
            }}
          >
            <FormattedMessage id="form_templates.add_question" />
          </TertiaryButton>
        </DropArea>
      )}
    </FormDropAreaWrapper>
  );
};

const ItemWrapper = styled.div`
  min-height: 40px;
  margin-top: var(--spacing-05);
  background: var(--white-100);
`;

export const FormItemWrapper: FC = ({ children }): JSX.Element => {
  return <ItemWrapper>{children}</ItemWrapper>;
};

export const ItemSelectionWrapper = styled.div<{
  isSelected: boolean;
}>`
  position: relative;
  border: 2px solid transparent;
  cursor: pointer;
  transition: box-shadow 0.3s, border-color 0.3s, -webkit-box-shadow 0.3s;
  &:hover {
    border-color: var(--blue-60);
    box-shadow: var(--shadow-16);
  }
  ${ifProp(
    'isSelected',
    css`
      border-color: var(--blue-60);
    `,
  )};
`;

export const ItemSelectionHeaderWrapper = styled(ItemSelectionWrapper)<{
  isSelected: boolean;
  hasHeaderImage: string | undefined;
}>`
  border: 2px solid transparent;
  ${ifNotProp(
    'hasHeaderImage',
    css`
      border-radius: 10px 10px 0px 0px;
    `,
  )};
  &:hover {
    border-color: var(--blue-60);
    box-shadow: var(--shadow-16);
  }
  ${ifProp(
    'isSelected',
    css`
      border-color: var(--blue-60);
    `,
  )};
`;
